import { useEffect, useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Loader2, Save, Bell, ChevronDown, MessageCircle, Mail, Smartphone, Info } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { LoadingState, PermissionDenied } from "@/components/states";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";

const CHANNEL_META = {
  in_app: { label: "In-app", icon: Bell },
  email: { label: "Email", icon: Mail },
  whatsapp: { label: "WhatsApp", icon: MessageCircle, note: "via future Pabbly" },
};
const PRIORITY_META = {
  critical: { label: "Critical", tone: "bg-error/10 text-error border-error/30", desc: "Delivered immediately, even during quiet hours." },
  important: { label: "Important", tone: "bg-warning/10 text-warning border-warning/30", desc: "Timely delivery within working hours." },
  routine: { label: "Routine", tone: "bg-success/10 text-success border-success/30", desc: "Can be bundled into digests." },
};

export default function Notifications() {
  const [state, setState] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [advanced, setAdvanced] = useState(false);
  const [baseline, setBaseline] = useState("");
  const [roles, setRoles] = useState([]);
  const [roleSel, setRoleSel] = useState("");

  useEffect(() => {
    apiFetch("/settings/notifications").then((d) => {
      setState(d); setCfg(d.config); setBaseline(JSON.stringify(d.config));
    }).catch((e) => toast.error(e.message));
    apiFetch("/roles").then((r) => { setRoles(r); if (r[0]) setRoleSel(r[0].name); }).catch(() => {});
  }, []);

  const dirty = cfg && JSON.stringify(cfg) !== baseline;
  useUnsavedChanges(dirty);

  if (!state) return <LoadingState label="Loading notification settings…" />;
  if (!state.can_manage) return <PermissionDenied message="You need the “Manage Notification Settings” permission to view organisation notification policy." />;

  const set = (path, value) => {
    setCfg((c) => {
      const next = structuredClone(c);
      let ref = next;
      for (let i = 0; i < path.length - 1; i++) ref = ref[path[i]] ??= {};
      ref[path[path.length - 1]] = value;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const d = await apiFetch("/settings/notifications", { method: "PUT", body: JSON.stringify({ config: cfg }) });
      setBaseline(JSON.stringify(d.config)); setCfg(d.config);
      toast.success("Notification settings saved");
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  return (
    <div className="animate-fade-up" data-testid="notifications-settings">
      <PageHeader eyebrow="Settings" title="Notifications"
        subtitle="Set how ContentOS notifies your team. No messages are sent yet — WhatsApp arrives with the future Pabbly bridge."
        actions={<Button onClick={save} disabled={!dirty || saving} className="gap-1.5" data-testid="notif-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
        </Button>} />

      <div className="mb-4 flex items-start gap-2 rounded-lg border border-accent/40 bg-accent-subtle px-4 py-3 text-sm" data-testid="notif-no-send-notice">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
        <span className="text-muted-foreground">These are policies only. Live sending (email/WhatsApp) is not connected in this release — nothing will be delivered.</span>
      </div>

      {/* channels */}
      <Card className="mb-5 p-5 shadow-soft">
        <h3 className="mb-1 font-heading text-lg font-semibold">Channels</h3>
        <p className="mb-3 text-xs text-muted-foreground">Turn a channel on to make it available across notification policies.</p>
        <div className="grid gap-3 sm:grid-cols-3">
          {Object.entries(CHANNEL_META).map(([key, meta]) => {
            const Icon = meta.icon;
            return (
              <label key={key} className="flex items-center justify-between rounded-lg border border-border p-3" data-testid={`channel-${key}`}>
                <span className="flex items-center gap-2 text-sm">
                  <Icon className="h-4 w-4 text-primary" /> {meta.label}
                  {meta.note && <span className="text-[10px] text-muted-foreground">{meta.note}</span>}
                </span>
                <Switch checked={!!cfg.channels_enabled?.[key]} onCheckedChange={(v) => set(["channels_enabled", key], v)}
                  disabled={key === "whatsapp"} data-testid={`channel-${key}-switch`} />
              </label>
            );
          })}
        </div>
      </Card>

      {/* default channels by priority */}
      <Card className="mb-5 p-5 shadow-soft">
        <h3 className="mb-1 font-heading text-lg font-semibold">Default channels by priority</h3>
        <p className="mb-3 text-xs text-muted-foreground">What ContentOS uses by default for each priority. Users can narrow this in their own preferences.</p>
        <div className="space-y-3">
          {Object.entries(PRIORITY_META).map(([pr, meta]) => (
            <div key={pr} className="rounded-lg border border-border p-3" data-testid={`priority-${pr}`}>
              <div className="mb-2 flex items-center gap-2">
                <Badge className={`border ${meta.tone}`}>{meta.label}</Badge>
                <span className="text-xs text-muted-foreground">{meta.desc}</span>
              </div>
              <div className="flex flex-wrap gap-4">
                {Object.entries(CHANNEL_META).map(([ch, cm]) => (
                  <label key={ch} className="flex items-center gap-2 text-sm">
                    <Switch checked={!!cfg.default_channels?.[pr]?.[ch]} onCheckedChange={(v) => set(["default_channels", pr, ch], v)}
                      disabled={!cfg.channels_enabled?.[ch]} data-testid={`priority-${pr}-${ch}`} />
                    {cm.label}
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* quiet hours */}
      <Card className="mb-5 p-5 shadow-soft">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-heading text-lg font-semibold">Quiet hours</h3>
            <p className="text-xs text-muted-foreground">Hold non-critical notifications overnight. Critical always breaks through.</p>
          </div>
          <Switch checked={!!cfg.quiet_hours?.enabled} onCheckedChange={(v) => set(["quiet_hours", "enabled"], v)} data-testid="quiet-hours-switch" />
        </div>
        {cfg.quiet_hours?.enabled && (
          <div className="mt-3 flex flex-wrap items-end gap-4" data-testid="quiet-hours-config">
            <div><Label className="text-xs">From</Label><Input type="time" value={cfg.quiet_hours.start} onChange={(e) => set(["quiet_hours", "start"], e.target.value)} className="mt-1 w-32" data-testid="quiet-start" /></div>
            <div><Label className="text-xs">To</Label><Input type="time" value={cfg.quiet_hours.end} onChange={(e) => set(["quiet_hours", "end"], e.target.value)} className="mt-1 w-32" data-testid="quiet-end" /></div>
            <label className="flex items-center gap-2 pb-2 text-sm"><Switch checked={cfg.quiet_hours.allow_critical !== false} onCheckedChange={(v) => set(["quiet_hours", "allow_critical"], v)} /> Allow critical to break through</label>
          </div>
        )}
      </Card>

      {/* advanced (progressive disclosure) */}
      <Collapsible open={advanced} onOpenChange={setAdvanced}>
        <CollapsibleTrigger asChild>
          <Button variant="outline" className="mb-3 gap-1.5" data-testid="notif-advanced-toggle">
            <ChevronDown className={`h-4 w-4 transition-transform ${advanced ? "rotate-180" : ""}`} /> Advanced configuration
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-5">
          <Card className="p-5 shadow-soft">
            <h3 className="mb-3 font-heading text-lg font-semibold">Delivery timing</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="flex items-center justify-between rounded-lg border border-border p-3 text-sm">Deliver in each recipient’s timezone<Switch checked={cfg.timezone_aware !== false} onCheckedChange={(v) => set(["timezone_aware"], v)} data-testid="timezone-aware" /></label>
              <label className="flex items-center justify-between rounded-lg border border-border p-3 text-sm">Routine only on working days<Switch checked={!!cfg.working_days_only} onCheckedChange={(v) => set(["working_days_only"], v)} data-testid="working-days-only" /></label>
            </div>
          </Card>
          <Card className="p-5 shadow-soft">
            <h3 className="mb-3 font-heading text-lg font-semibold">Reminders &amp; digests</h3>
            <div className="grid gap-4 sm:grid-cols-3">
              <div><Label className="text-xs">Reminder lead time (min)</Label><Input type="number" min="0" value={cfg.reminders?.default_lead_minutes ?? 60} onChange={(e) => set(["reminders", "default_lead_minutes"], Number(e.target.value))} className="mt-1" data-testid="reminder-lead" /></div>
              <div><Label className="text-xs">Daily digest time</Label><Input type="time" value={cfg.digest?.daily_time || "08:00"} onChange={(e) => set(["digest", "daily_time"], e.target.value)} className="mt-1" data-testid="digest-daily-time" /></div>
              <div>
                <Label className="text-xs">Weekly digest day</Label>
                <Select value={cfg.digest?.weekly_day || "Monday"} onValueChange={(v) => set(["digest", "weekly_day"], v)}>
                  <SelectTrigger className="mt-1" data-testid="digest-weekly-day"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <label className="mt-3 flex items-center gap-2 text-sm"><Switch checked={cfg.digest?.routine_bundling !== false} onCheckedChange={(v) => set(["digest", "routine_bundling"], v)} data-testid="routine-bundling" /> Bundle routine notifications into digests</label>
          </Card>
          <Card className="p-5 shadow-soft">
            <div className="flex items-center justify-between">
              <div><h3 className="font-heading text-lg font-semibold">Escalation readiness</h3>
                <p className="text-xs text-muted-foreground">Prepare escalation of unacknowledged critical items (activates with workflows).</p></div>
              <Switch checked={!!cfg.escalation?.enabled} onCheckedChange={(v) => set(["escalation", "enabled"], v)} data-testid="escalation-switch" />
            </div>
            {cfg.escalation?.enabled && (
              <div className="mt-3 flex flex-wrap items-end gap-4">
                <div><Label className="text-xs">Escalate after (min)</Label><Input type="number" min="0" value={cfg.escalation?.after_minutes ?? 120} onChange={(e) => set(["escalation", "after_minutes"], Number(e.target.value))} className="mt-1 w-32" data-testid="escalation-after" /></div>
              </div>
            )}
          </Card>
          <Card className="p-5 shadow-soft">
            <h3 className="mb-1 font-heading text-lg font-semibold">Event catalogue</h3>
            <p className="mb-3 text-xs text-muted-foreground">Events future modules will emit into this one notification engine. Inactive until their modules ship.</p>
            <div className="flex flex-wrap gap-2" data-testid="event-catalogue">
              {state.events.map((e) => (
                <span key={e.key} className="flex items-center gap-1.5 rounded-full border border-border px-2.5 py-1 text-xs">
                  <span className="font-mono text-[10px] text-muted-foreground">{e.key}</span>
                  <Badge className={`border ${PRIORITY_META[e.priority]?.tone || ""}`}>{e.priority}</Badge>
                </span>
              ))}
            </div>
          </Card>
          <Card className="p-5 shadow-soft" data-testid="role-defaults">
            <h3 className="mb-1 font-heading text-lg font-semibold">Role defaults</h3>
            <p className="mb-3 text-xs text-muted-foreground">Per-role defaults sit between the organisation policy and each person’s own preference (Organisation → Role → User → mandatory Critical). Leave a channel off to inherit the organisation default.</p>
            <div className="mb-3 max-w-xs">
              <Label className="text-xs">Role</Label>
              <Select value={roleSel} onValueChange={setRoleSel}>
                <SelectTrigger className="mt-1" data-testid="role-defaults-select"><SelectValue /></SelectTrigger>
                <SelectContent>{roles.map((r) => <SelectItem key={r.name} value={r.name}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {roleSel && (
              <div className="space-y-3" data-testid={`role-defaults-${roleSel}`}>
                {Object.entries(PRIORITY_META).map(([pr, meta]) => (
                  <div key={pr} className="rounded-lg border border-border p-3">
                    <Badge className={`mb-2 border ${meta.tone}`}>{meta.label}</Badge>
                    <div className="flex flex-wrap gap-4">
                      {Object.entries(CHANNEL_META).map(([ch, cm]) => (
                        <label key={ch} className="flex items-center gap-2 text-sm">
                          <Switch
                            checked={!!cfg.role_defaults?.[roleSel]?.[pr]?.[ch]}
                            disabled={!cfg.channels_enabled?.[ch]}
                            aria-disabled={!cfg.channels_enabled?.[ch]}
                            onCheckedChange={(v) => set(["role_defaults", roleSel, pr, ch], v)}
                            data-testid={`role-${pr}-${ch}`} />
                          {cm.label}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
                <div className="flex flex-wrap gap-4 rounded-lg border border-border p-3">
                  <label className="flex items-center gap-2 text-sm">
                    <Switch checked={cfg.role_defaults?.[roleSel]?.quiet_hours_inherit !== false}
                      onCheckedChange={(v) => set(["role_defaults", roleSel, "quiet_hours_inherit"], v)} data-testid="role-quiet-inherit" />
                    Inherit organisation quiet hours
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <Switch checked={!!cfg.role_defaults?.[roleSel]?.digest}
                      onCheckedChange={(v) => set(["role_defaults", roleSel, "digest"], v)} data-testid="role-digest" />
                    Bundle routine into digests for this role
                  </label>
                </div>
              </div>
            )}
          </Card>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
